import React, { useRef, useState } from "react";
import { HeartPulse, Camera } from "lucide-react";
import EnfermeriaModule from "@/components/app/Enfermeria";
import NurseExtras, { NURSE_SECTIONS } from "@/components/app/NurseExtras";
import { NavLink, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const NurseProfileCard = () => {
  const { user, checkAuth } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const roleWord = user?.title === "enfermera" ? "Enfermera" : "Enfermero";
  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/coord/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth();
      toast.success("Foto de perfil actualizada");
    } catch { toast.error("No se pudo subir la foto"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-4 bg-white border border-nardo-200 rounded-2xl p-4 mb-6 shadow-sm" data-testid="nurse-profile-card">
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="relative group shrink-0 disabled:opacity-60" data-testid="nurse-photo-edit" title="Cambiar foto de perfil">
        {user?.photo_url ? (
          <img src={`${API}${user.photo_url}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-rose-200" />
        ) : (
          <span className="w-16 h-16 rounded-full flex items-center justify-center bg-rose-100 text-rose-600 text-xl font-bold border-2 border-rose-200">{(user?.name || "E").slice(0, 1).toUpperCase()}</span>
        )}
        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center border-2 border-white group-hover:bg-rose-700 transition-colors"><Camera className="w-3 h-3" /></span>
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="nurse-photo-input" />
      <div className="min-w-0">
        <div className="font-display font-bold text-lg text-navy truncate" data-testid="nurse-name">{user?.name || "Enfermería"}</div>
        <div className="text-sm text-rose-700 font-medium">{roleWord} escolar</div>
        <div className="text-[11px] text-nardo-400 truncate">{user?.email}</div>
      </div>
    </div>
  );
};

export default function Enfermeria() {
  const { user } = useAuth();
  const isNurse = user?.role === "staff" && user?.staff_kind === "enfermeria";
  const sec = useParams().sec || "atencion";
  return (
    <div className="max-w-7xl mx-auto" data-testid="enfermeria-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2"><span className="w-8 h-px bg-navy" /> Salud escolar</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy flex items-center gap-3"><HeartPulse className="w-8 h-8 text-navy-light" strokeWidth={1.5} /> {isNurse ? "Portal de Enfermería" : "Enfermería"}</h1>
      </div>
      {isNurse && <NurseProfileCard />}
      {!isNurse && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="enf-section-menu">
          {NURSE_SECTIONS.map(({ key, label, icon: I }) => (
            <NavLink key={key} to={`/enfermeria/${key}`} data-testid={`enf-sec-${key}`} className={`inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg font-semibold transition-colors ${sec === key ? "bg-rose-500 text-white shadow" : "bg-white border border-nardo-200 text-nardo-600 hover:border-rose-300"}`}><I className="w-4 h-4" /> {label}</NavLink>
          ))}
        </div>
      )}
      {sec === "atencion" || sec === "pesos" ? <EnfermeriaModule forceView={sec === "pesos" ? "pesos" : "actividad"} /> : <NurseExtras section={sec} />}
    </div>
  );
}
